import { useWishlistEntryPoints } from 'src/lib/optimizely/experiments/use-wishlist-entry-points-visible'
import { Footer } from '@awaze/design-system.compositions.footer'
import {
  HeaderExtended,
  ResponsiveHeader,
} from '@awaze/design-system.compositions.responsive-header'
import { renderContentfulRichText } from '@awaze/mfe-common.lib.contentful/dist/api/utils/render-richtext'
import React, { useContext } from 'react'
import { useLocations } from 'src/components/LocationSelector/useLocations'
import { richTextOptions } from 'src/lib/contentful/contentful.utils'
import { GlobalProps } from 'src/utils/globalProps'
import { Container } from '@awaze/design-system.components.container'
import { SearchFormConfig } from '@awaze/design-system.compositions.search-form'
import { IHeaderOptionsModuleFields } from 'contentful/types/contentful-modules'
import { Hero } from '@/components/Hero/Hero'
import { SearchFormComponent } from 'src/components/SearchForm/SearchForm'
import { AWSAccountBanner } from 'src/components/Utils/AWSAccountBanner'
import { PreviewBanner } from 'src/components/Utils/PreviewBanner'
import { BreadcrumbItems } from 'src/utils/breadcrumb/breadcrumb-utils'
import { GlobalStyling } from 'src/utils/globalstyle'
import {
  IFooterFields,
  IHeaderFields,
} from '../../@types/generated/contentful-global'
import Breadcrumb from '@/components/Breadcrumbs/Breadcrumb'
import useDatalayerOnClick from 'src/utils/dataLayer/DatalayerOnClick/useDatalayerOnClick'
import useOptimizelyExperimentsDatalayer from 'src/utils/dataLayer/useOptimizelyExperimentsDatalayer'
import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import { unwrapMenu } from './GenericPageLayoutMap'
import { useGuestAuth } from '@awaze/mfe-common.contexts.guest-auth'

export type GenericPageLayoutProps = {
  children: React.ReactNode
  isPreview?: boolean
  contentfulId?: string
  header: IHeaderFields
  footer: IFooterFields
  searchFormConfig: SearchFormConfig
  headerOptions?: IHeaderOptionsModuleFields
  newAWSAccount?: boolean
  breadcrumbs: BreadcrumbItems[]
  showPhoneIcon: boolean
  brand: Brand
}

export const GenericPageLayout: React.FC<GenericPageLayoutProps> = ({
  isPreview,
  contentfulId,
  brand,
  header,
  footer,
  searchFormConfig,
  headerOptions,
  newAWSAccount,
  breadcrumbs,
  showPhoneIcon,
  children,
}) => {
  const { contentfulLocale } = useContext(GlobalProps)
  const locations = useLocations()
  const { session } = useGuestAuth()

  //Datalayer for Optimizely experiments
  useOptimizelyExperimentsDatalayer()

  const { ref } = useDatalayerOnClick()

  // Optimizely experiment for wishlist icon
  const wishListIcon = unwrapMenu(header.desktopMenu?.fields?.menuItems).find(
    (item) => item.type === 'wishlist',
  )
  const showWishlistEntryPoints = useWishlistEntryPoints()
  const shouldShowWishlistIcon =
    !!wishListIcon && !!wishListIcon.link?.href && showWishlistEntryPoints

  return (
    <>
      <div ref={ref}>
        {isPreview && <PreviewBanner contentfulId={contentfulId} />}
        {newAWSAccount && <AWSAccountBanner />}
        <ResponsiveHeader
          user={session?.user}
          wishlist={{
            displayWishlistIcon: shouldShowWishlistIcon,
            label: wishListIcon?.link?.displayText || '',
            href: wishListIcon?.link?.href || '',
          }}
          phoneNumber={{
            displayPhoneIcon: showPhoneIcon,
            href: header.desktopMenu?.fields?.menuItems?.[0]?.fields?.link
              ?.fields?.href,
            label: header.desktopMenu?.fields?.menuItems?.[0]?.fields?.title,
          }}
          brand={brand}
          locale={contentfulLocale}
          menuItems={{
            mobileMenu: unwrapMenu(header.mobileMenu?.fields?.menuItems) ?? [],
            desktopMenu:
              unwrapMenu(header.desktopMenu?.fields?.menuItems) ?? [],
          }}
          labels={{
            menuButtonText: header.menuButtonText,
            closeButtonText: header.closeButtonText,
          }}
          showMegaMenu
        />
        {headerOptions && (
          <Hero
            searchFormConfig={searchFormConfig}
            headerOptions={headerOptions}
            alertBanner={header.alertBanner}
          />
        )}
        {!headerOptions && (
          <HeaderExtended isFlush={true}>
            <Container>
              <SearchFormComponent config={searchFormConfig} isSummary={true} />
            </Container>
          </HeaderExtended>
        )}
        {breadcrumbs && <Breadcrumb breadcrumbs={breadcrumbs} />}
        <div className="content-container" id="content-container">
          {children}
        </div>

        <Footer
          locationSelect={locations}
          labels={{
            menu: {
              menuItems: unwrapMenu(footer.menu?.fields?.menuItems) ?? [],
            },
            panels:
              footer?.panels?.map((p) => ({
                cta: {
                  text: p.fields?.cta?.fields?.text,
                  title: p.fields?.cta?.fields?.title,
                  icon: p.fields?.cta?.fields?.icon,
                  link: p.fields?.cta?.fields?.link,
                },
                text: p.fields?.text,
                title: p.fields?.title,
                icon: p.fields?.icon,
                secondaryText: p.fields?.secondaryText,
              })) ?? [],
            socialMediaIcons:
              footer?.socialMediaIcons?.map((i) => ({
                displayText: i.fields?.displayText,
                href: i.fields?.href,
                openInNew: i.fields?.openInNew,
                rel: i.fields?.rel,
              })) ?? [],
            termsAndConditions: {
              displayText: footer?.termsAndConditions?.fields?.displayText,
              href: footer?.termsAndConditions?.fields?.href,
              openInNew: footer?.termsAndConditions?.fields?.openInNew,
              rel: footer?.termsAndConditions?.fields?.rel,
            },
            companyInformation: renderContentfulRichText(
              footer?.companyInformation,
              richTextOptions,
            ) as React.ReactElement,
          }}
          styleProps={{
            accordion: {
              theme: {
                accordion: 'dark',
              },
            },
          }}
        />
      </div>
      <GlobalStyling />
    </>
  )
}
